import { useState } from "react";
import { Link } from "react-router-dom";
import {
  Menu, Wallet, GraduationCap,
  LogIn, User as UserIcon, Lock,
  Users, Trophy, Settings, Library as LibraryIcon,
  Bug, Rocket, Brain,
} from "lucide-react";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import React from "react";
import { useAuth } from "@/hooks/useAuth";
import { useUserRole } from "@/hooks/useUserRole";
import { cn } from "@/lib/utils";
import { useRightPanel, type PanelId } from "@/components/right-panel/useRightPanel";

// Every entry opens a right-side panel — no navigation. Panel ids map
// to the registry in RightPanelHost.
type Action = {
  panel: PanelId;
  label: string;
  Icon: React.ComponentType<{ className?: string }>;
  group: string;
  hint?: string;
  guestOk?: boolean;
};

const BASE_ITEMS: Action[] = [
  // AI & Money
  { panel: "wallet",    label: "Wallet",    Icon: Wallet,         group: "AI & Money", hint: "View balance & usage", guestOk: true },
  { panel: "tutor",     label: "Tutor",     Icon: GraduationCap,  group: "AI & Money", hint: "Ask anything about HackerLabs", guestOk: true },
  { panel: "memory",    label: "Memory",    Icon: Brain,          group: "AI & Money", hint: "Manage AI memory" },

  // Community
  { panel: "squads",    label: "Squads",    Icon: Users,          group: "Community" },
  { panel: "leaders",   label: "Leaders",   Icon: Trophy,         group: "Community" },
  { panel: "hackathon", label: "Hackathon", Icon: Rocket,         group: "Community", guestOk: true },

  // Account
  { panel: "settings",  label: "Settings",  Icon: Settings,       group: "Account" },
  { panel: "bugs",      label: "Bug report",Icon: Bug,            group: "Account", guestOk: true },
];

const ADMIN_ITEMS: Action[] = [
  { panel: "library",   label: "Library",   Icon: LibraryIcon,    group: "Admin", guestOk: true },
];

const GROUPS = ["AI & Money", "Community", "Account", "Admin"] as const;

export default function UnifiedMenu() {
  const { user } = useAuth();
  const { isAdmin } = useUserRole();
  const [menuOpen, setMenuOpen] = useState(false);
  const { openPanel } = useRightPanel();

  const choose = (panel: PanelId) => {
    setMenuOpen(false);
    // Tiny defer so the sheet close animation doesn't clash with the
    // new sheet opening.
    setTimeout(() => openPanel(panel), 80);
  };

  return (
    <>
      {/* Single trigger — top-right only. */}
      <button
        type="button"
        onClick={() => setMenuOpen(true)}
        aria-label="Open menu"
        className="fixed top-3 right-3 z-40 h-10 w-10 grid place-items-center rounded-full border border-border bg-card/80 backdrop-blur hover:border-primary/50 hover:bg-card shadow-lg transition-colors"
      >
        <Menu className="w-4 h-4" />
      </button>

      <Sheet open={menuOpen} onOpenChange={setMenuOpen}>
        <SheetContent side="right" className="w-[280px] sm:w-[320px] p-0 flex flex-col">
          <SheetHeader className="p-4 border-b border-border">
            <SheetTitle className="font-mono text-sm flex items-center gap-2">
              <Menu className="w-4 h-4 text-primary" /> Menu
            </SheetTitle>
          </SheetHeader>

          <div className="flex-1 overflow-y-auto p-3">
            {!user && (
              <div className="mb-4 p-3 rounded-xl border border-border bg-card/40">
                <p className="text-[11px] font-mono text-muted-foreground mb-2">
                  Sign in to access Squads, Leaders, Settings and more.
                </p>
                <Link
                  to="/signup"
                  onClick={() => setMenuOpen(false)}
                  className="h-9 w-full flex items-center justify-center gap-2 rounded-lg bg-primary text-primary-foreground text-xs font-mono font-semibold hover:opacity-90"
                >
                  <LogIn className="w-4 h-4" /> Sign in to unlock
                </Link>
              </div>
            )}

            {GROUPS.map((group) => {
              const all = group === "Admin" ? ADMIN_ITEMS : BASE_ITEMS;
              const items = all.filter((i) => i.group === group);
              if (group === "Admin" && !isAdmin) return null;
              if (!items.length) return null;
              return (
                <div key={group} className="mb-4">
                  <div className="px-2 pb-1 text-[9px] uppercase tracking-wider font-mono text-muted-foreground/70">
                    {group}
                  </div>
                  <div className="flex flex-col gap-0.5">
                    {items.map((item) => {
                      const Icon = item.Icon;
                      const locked = !user && !item.guestOk;
                      const inner = (
                        <>
                          <Icon className="w-4 h-4 shrink-0" />
                          <span className="text-xs font-mono truncate flex-1 text-left">{item.label}</span>
                          {locked && <Lock className="w-3 h-3 opacity-40" />}
                        </>
                      );
                      const className = cn(
                        "h-9 px-2 flex items-center gap-2 rounded-lg w-full",
                        "text-muted-foreground hover:text-foreground hover:bg-secondary/70 transition-colors",
                        locked && "opacity-50 pointer-events-none",
                      );
                      return (
                        <button
                          key={item.label}
                          type="button"
                          className={className}
                          onClick={() => choose(item.panel)}
                        >
                          {inner}
                        </button>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Auth row pinned to bottom */}
          <div className="border-t border-border p-3">
            {user ? (
              <Link
                to="/account"
                onClick={() => setMenuOpen(false)}
                className="h-10 px-3 flex items-center gap-2 rounded-lg border border-border hover:border-primary/50 text-xs font-mono"
              >
                <UserIcon className="w-4 h-4" />
                <span className="truncate">{user.email?.split("@")[0] ?? "Account"}</span>
              </Link>
            ) : (
              <Link
                to="/signup"
                onClick={() => setMenuOpen(false)}
                className="h-10 px-3 flex items-center gap-2 rounded-lg bg-primary text-primary-foreground text-xs font-mono font-semibold hover:opacity-90"
              >
                <LogIn className="w-4 h-4" /> Sign in
              </Link>
            )}
          </div>
        </SheetContent>
      </Sheet>
    </>
  );
}
